import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
interface SectionProps {
  id?: string;
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div';
}
export function Section({
  id,
  children,
  className = '',
  as = 'section'
}: SectionProps) {
  const Tag = as;
  return <Tag id={id} className={`px-5 sm:px-8 ${className}`} data-id="element-79" data-mp="3dk7vltjh8lpo">
      <div className="mx-auto w-full max-w-6xl" data-id="element-80" data-mp="2pt2hbit6q1hh">{children}</div>
    </Tag>;
}
export function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-pine" data-id="element-81" data-mp="2kw28x18v4mn7">{children}</p>;
}
interface HeadingProps {
  children: React.ReactNode;
  level?: 2 | 3;
  className?: string;
  id?: string;
}
export function Heading({
  children,
  level = 2,
  className = '',
  id
}: HeadingProps) {
  const Tag = level === 2 ? 'h2' : 'h3';
  const size = level === 2 ? 'text-3xl sm:text-4xl leading-tight' : 'text-xl sm:text-2xl leading-snug';
  return <Tag id={id} className={`font-display font-semibold text-ink ${size} ${className}`} data-id="element-82" data-mp="x4z07m9krf5j">
      {children}
    </Tag>;
}
export function Prose({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`space-y-4 text-[17px] leading-relaxed text-cocoa/90 ${className}`} data-id="element-83" data-mp="17350y0jnaf8t">
      {children}
    </div>;
}
interface ButtonLinkProps {
  to?: string;
  href?: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
}
const buttonBase = 'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pine';
const variants = {
  primary: 'bg-cocoa text-cream hover:bg-ink',
  secondary: 'border border-cocoa/25 bg-transparent text-cocoa hover:border-cocoa/60 hover:bg-sand/60'
};
export function ButtonLink({
  to,
  href,
  children,
  variant = 'primary',
  className = ''
}: ButtonLinkProps) {
  const classes = `${buttonBase} ${variants[variant]} ${className}`;
  if (to) {
    return <Link to={to} className={classes} data-id="element-84" data-mp="xyrwp1a2j9ag">
        {children}
      </Link>;
  }
  return <a href={href} className={classes} {...href && href.startsWith('http') ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} data-id="element-85" data-mp="1crbnd2g17ob0">
      {children}
    </a>;
}
export function TextLink({
  to,
  href,
  children
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
}) {
  const classes = 'font-medium text-pine underline decoration-pine/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-cocoa hover:decoration-cocoa';
  if (to) {
    return <Link to={to} className={classes} data-id="element-86" data-mp="24gevitb1hisx">
        {children}
      </Link>;
  }
  return <a href={href} className={classes} target="_blank" rel="noopener noreferrer" data-id="element-87" data-mp="20fd24t3t00yv">
      {children}
    </a>;
}
export function NextPage({
  to,
  label,
  description
}: {
  to: string;
  label: string;
  description: string;
}) {
  return <Link to={to} className="group flex items-start justify-between gap-6 rounded-2xl border border-cocoa/15 bg-paper p-6 transition-colors duration-150 ease-out hover:border-pine/50" data-id="element-88" data-mp="2cyqgpg7rp7yu">
      <span data-id="element-89" data-mp="230kknf8n6w1w">
        <span className="block font-display text-lg font-semibold text-ink" data-id="element-90" data-mp="23019qihet7xd">{label}</span>
        <span className="mt-1 block text-[15px] leading-relaxed text-cocoa/80" data-id="element-91" data-mp="2qqerng6a5qe8">{description}</span>
      </span>
      <ArrowRightIcon className="mt-1 h-5 w-5 shrink-0 text-pine transition-transform duration-150 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-92" data-mp="2vnnfnxe40one" />
    </Link>;
}